import { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, checkIsMagicLink, completeMagicLink } from './firebase';

const AppContext = createContext(null);

const MOCK_FRIENDS = [
  { id: '1', name: 'Omar Farooq',     initials: 'OF', forgiven: true,  time: '11:42 PM', mutual: true },
  { id: '2', name: 'Fatima Malik',    initials: 'FM', forgiven: true,  time: '10:15 PM', mutual: true },
  { id: '3', name: 'Ibrahim Hassan',  initials: 'IH', forgiven: false, time: null,        mutual: true },
  { id: '4', name: 'Aisha Siddiqui', initials: 'AS', forgiven: false, time: null,        mutual: true },
];

const MOCK_PENDING = [
  { id: '5', name: 'Yusuf Ali', initials: 'YA' }
];

export function AppProvider({ children }) {
  const [screen, setScreen]                   = useState('onboard');
  const [tab, setTab]                         = useState('feed');
  const [user, setUser]                       = useState(null);
  const [authLoading, setAuthLoading]         = useState(true);
  const [magicLinkError, setMagicLinkError]   = useState(null);
  const [forgiven, setForgiven]               = useState(false);
  const [friends, setFriends]                 = useState(MOCK_FRIENDS);
  const [pendingRequests, setPendingRequests] = useState(MOCK_PENDING);
  const [notifyCircle, setNotifyCircle]       = useState(true);
  const [toast, setToast]                     = useState({ visible: false, msg: '' });
  const [authMode, setAuthMode]               = useState('signup');

  // ── 1. Handle magic link return first, before auth listener ──────────────
  useEffect(() => {
    async function handleMagicLink() {
      if (!checkIsMagicLink()) {
        // Not a magic link — let onAuthStateChanged handle it
        return;
      }
      try {
        const firebaseUser = await completeMagicLink();
        await handleFirebaseUser(firebaseUser);
      } catch (err) {
        console.error('Magic link error:', err);
        setMagicLinkError(err.message || 'Sign-in link expired or already used.');
        setAuthLoading(false);
      }
    }
    handleMagicLink();
  }, []); // eslint-disable-line

  // ── 2. Auth state listener ────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async firebaseUser => {
      // If this is a magic link URL, let the effect above handle it
      if (checkIsMagicLink()) return;

      if (firebaseUser) {
        await handleFirebaseUser(firebaseUser);
      } else {
        setUser(null);
        setScreen('onboard');
        setAuthLoading(false);
      }
    });
    return unsub;
  }, []); // eslint-disable-line

  // ── 3. Midnight reset ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const timer = setTimeout(() => {
      setForgiven(false);
      setFriends(prev => prev.map(f => ({ ...f, forgiven: false, time: null })));
    }, midnight - now);
    return () => clearTimeout(timer);
  }, [user, forgiven]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  function getInitials(name) {
    return name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  }

  function generateInviteCode(name) {
    return `${name.toLowerCase().split(' ')[0].replace(/[^a-z]/g, '')}-${Math.random().toString(36).substr(2, 4)}`;
  }

  async function handleFirebaseUser(firebaseUser) {
    try {
      const ref = doc(db, 'users', firebaseUser.uid);
      const snap = await getDoc(ref);

      let userData;

      if (snap.exists()) {
        userData = snap.data();
      } else {
        const savedName = window.localStorage.getItem('mercyNameForSignIn')
          || (firebaseUser.displayName)
          || firebaseUser.email.split('@')[0];
        window.localStorage.removeItem('mercyNameForSignIn');

        userData = {
          name: savedName,
          email: firebaseUser.email,
          initials: getInitials(savedName),
          inviteCode: generateInviteCode(savedName),
          notifyCircle: true,
          createdAt: serverTimestamp(),
        };
        await setDoc(ref, userData);
      }

      setUser({
        uid: firebaseUser.uid,
        name: userData.name,
        email: userData.email,
        initials: userData.initials || getInitials(userData.name),
        inviteCode: userData.inviteCode,
      });
      setNotifyCircle(userData.notifyCircle ?? true);
      setScreen('main');
    } catch (err) {
      console.error('Firestore error:', err);
      setMagicLinkError('Could not load your account. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  function showToast(msg) {
    setToast({ visible: true, msg });
    setTimeout(() => setToast({ visible: false, msg: '' }), 3000);
  }

  async function logout() {
    await signOut(auth);
    setUser(null);
    setForgiven(false);
    setScreen('onboard');
    setTab('feed');
  }

  function doForgive() {
    if (forgiven) return;
    setForgiven(true);
  }

  function acceptRequest(id) {
    const req = pendingRequests.find(r => r.id === id);
    if (!req) return;
    setPendingRequests(prev => prev.filter(r => r.id !== id));
    setFriends(prev => [...prev, { ...req, forgiven: false, time: null, mutual: true }]);
    showToast(`${req.name} added to your circle`);
  }

  function declineRequest(id) {
    setPendingRequests(prev => prev.filter(r => r.id !== id));
  }

  return (
    <AppContext.Provider value={{
      screen, setScreen,
      tab, setTab,
      user, authLoading, magicLinkError,
      authMode, setAuthMode,
      forgiven, doForgive,
      friends, setFriends,
      pendingRequests,
      acceptRequest, declineRequest,
      notifyCircle, setNotifyCircle,
      toast, showToast,
      logout,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
